import { Link } from 'react-router';
import {
  useGetCivicRegions,
  useGetCivicTenants,
  type RegionFeature,
  type Tenant,
} from '@cdf/api-client-react';
import {
  AlertCircleIcon,
  ArrowRightIcon,
  MapIcon,
  MapPinIcon,
  SproutIcon,
  CameraIcon,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  getFarmAddress,
  resolveRegionKind,
} from '@/lib/registered-farms';

interface RegisteredFarm {
  regionId: string;
  regionName: string;
  regionSlug: string;
  tenantName: string;
  tenantSlug: string;
  address?: string;
  hasBoundary: boolean;
}

function buildRegisteredFarms(
  features: RegionFeature[] | undefined,
  tenants: Tenant[] | undefined,
): RegisteredFarm[] {
  const tenantById = new Map(
    (tenants ?? [])
      .filter((t): t is Tenant & { id: string } => typeof t.id === 'string' && t.id.length > 0)
      .map((t) => [t.id, t]),
  );

  const farms: RegisteredFarm[] = [];

  for (const feature of features ?? []) {
    const props = feature.properties;
    if (!props?.id || !props.slug || !props.name) continue;

    const tenant = props.tenantId ? tenantById.get(props.tenantId) : undefined;
    const kind = resolveRegionKind(
      props.slug,
      props.name,
      tenant?.type,
      tenant?.slug,
    );
    if (kind !== 'farm') continue;

    farms.push({
      regionId: props.id,
      regionName: props.name,
      regionSlug: props.slug,
      tenantName: tenant?.name ?? props.name,
      tenantSlug: tenant?.slug ?? '',
      address: getFarmAddress(props.slug),
      hasBoundary: Boolean(feature.geometry),
    });
  }

  farms.sort((a, b) => a.regionName.localeCompare(b.regionName));
  return farms;
}

function FarmCard({ farm }: { farm: RegisteredFarm }) {
  const mapTo = `/map?regionSlug=${encodeURIComponent(farm.regionSlug)}`;
  const reportTo = `/report?purpose=farms&region=${encodeURIComponent(farm.regionId)}`;

  return (
    <Card className="group relative overflow-hidden transition-shadow hover:shadow-md">
      <CardContent className="flex h-full flex-col gap-4 p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-900 ring-1 ring-amber-200 transition-colors group-hover:bg-amber-700 group-hover:text-amber-50 dark:bg-amber-950 dark:text-amber-200 dark:ring-amber-900">
            <SproutIcon className="h-5 w-5" aria-hidden />
          </div>
          <Badge
            variant="secondary"
            className="bg-amber-100 font-semibold uppercase tracking-wide text-amber-900 hover:bg-amber-100 dark:bg-amber-950 dark:text-amber-200"
          >
            Registered farm
          </Badge>
        </div>

        <div className="flex flex-1 flex-col gap-1.5">
          <h3 className="text-lg font-semibold leading-snug">{farm.regionName}</h3>
          <p className="text-sm text-muted-foreground">{farm.tenantName}</p>
          {farm.address ? (
            <p className="mt-1 flex items-start gap-1.5 text-sm leading-relaxed text-foreground/90">
              <MapPinIcon
                className="mt-0.5 h-4 w-4 shrink-0 text-amber-800 dark:text-amber-300"
                aria-hidden
              />
              <span>{farm.address}</span>
            </p>
          ) : (
            <p className="mt-1 text-sm italic text-muted-foreground">
              Address details coming soon.
            </p>
          )}
          {farm.hasBoundary ? (
            <p className="text-xs text-muted-foreground">
              Property boundary available on the Map Explorer.
            </p>
          ) : null}
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button asChild size="sm" className="w-full sm:w-auto">
            <Link to={mapTo}>
              <MapIcon className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              View on Map
              <ArrowRightIcon className="ml-1.5 h-3.5 w-3.5" aria-hidden />
            </Link>
          </Button>
          <Button asChild size="sm" variant="outline" className="w-full sm:w-auto">
            <Link to={reportTo}>
              <CameraIcon className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              Report Farm Issue
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function FarmCardSkeleton() {
  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-6">
        <div className="flex items-start justify-between gap-3">
          <Skeleton className="h-11 w-11 rounded-lg" />
          <Skeleton className="h-5 w-28 rounded-full" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-4 w-full" />
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Skeleton className="h-9 w-full sm:w-36" />
          <Skeleton className="h-9 w-full sm:w-40" />
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Public listing of registered farm properties for the /for/farms purpose page.
 */
export function RegisteredFarmsList() {
  const {
    data: regionsData,
    isLoading: regionsLoading,
    isError: regionsError,
    refetch: refetchRegions,
    isFetching: regionsFetching,
  } = useGetCivicRegions();
  const {
    data: tenantsData,
    isLoading: tenantsLoading,
    isError: tenantsError,
    refetch: refetchTenants,
    isFetching: tenantsFetching,
  } = useGetCivicTenants();

  const isLoading = regionsLoading || tenantsLoading;
  const isError = regionsError || tenantsError;
  const isFetching = regionsFetching || tenantsFetching;

  const farms = buildRegisteredFarms(regionsData?.features, tenantsData?.tenants);

  return (
    <section className="bg-muted/30" aria-labelledby="registered-farms-heading">
      <div className="mx-auto max-w-5xl px-4 py-16 md:py-20">
        <div className="mb-10 text-center">
          <h2
            id="registered-farms-heading"
            className="text-2xl font-bold tracking-tight sm:text-3xl"
          >
            Registered Farms & Properties
          </h2>
          <p className="mx-auto mt-2 max-w-lg text-muted-foreground">
            Explore farm boundaries on the map and report issues against registered properties.
          </p>
        </div>

        {isLoading ? (
          <div className="grid gap-6 sm:grid-cols-2">
            <FarmCardSkeleton />
          </div>
        ) : isError ? (
          <div className="mx-auto flex max-w-md flex-col items-center gap-4 rounded-xl border border-border bg-background px-6 py-10 text-center">
            <AlertCircleIcon className="h-10 w-10 text-destructive" aria-hidden />
            <div className="space-y-1">
              <p className="font-semibold">Couldn&rsquo;t load farms</p>
              <p className="text-sm text-muted-foreground">
                Check your connection and try again.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isFetching}
              onClick={() => {
                void refetchRegions();
                void refetchTenants();
              }}
            >
              Retry
            </Button>
          </div>
        ) : farms.length === 0 ? (
          <div className="mx-auto max-w-md rounded-xl border border-dashed border-border bg-background px-6 py-10 text-center">
            <SproutIcon className="mx-auto h-10 w-10 text-muted-foreground" aria-hidden />
            <p className="mt-3 font-semibold">No registered farms yet</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Farm properties with mapped boundaries will appear here when available.
            </p>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2">
            {farms.map((farm) => (
              <FarmCard key={farm.regionId} farm={farm} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
